/**
 * シリアライズ可能なゲーム状態（CLAUDE.md §4.4）。
 * ここに入っているものだけがセーブされる。Map / Set / 関数を入れないこと。
 * 派生物（跳躍グラフなど）は World 側のキャッシュに置く。
 */
import building from '../data/floors.json';
import catsData from '../data/cats.json';
import historyData from '../data/histories.json';
import salvageData from '../data/salvage.json';
import fixtureData from '../data/fixtures.json';
import nameData from '../data/names.json';
import memoryData from '../data/memories.json';
import { EMPTY, Grid, SALVAGE, STRUCTURE, TILE, keyOf, setTile } from './grid';
import { JumpParams } from './graph';
import { ItemId } from './items';
import { MaterialBundle, Materials, emptyMaterials } from './materials';
import { RngState, createRng } from './rng';

export const STATE_VERSION = 10;

export interface Species extends JumpParams {
  id: string;
  name: string;
  /** 移動速度の倍率 */
  speed: number;
  /** 外から来る時の選ばれやすさ */
  arrivalWeight: number;
  /**
   * 高い所を怖がる度合い（§3.3 子猫）。
   * グラフを変えるのではなく、経路のコストを上げる。
   * 「行けるけど行きたがらない」を数値ではなく振る舞いで出すため。
   */
  heightFear?: number;
  /** 他の猫が多い経路を避ける度合い（§3.3 臆病な猫） */
  crowdFear?: number;
  /**
   * 前足が届く高さ（マス）。廃墟の飾りを叩き落とせる範囲（§3.3）。
   * 跳ぶのが得意な猫ほど高い所の物を落とせる。
   * 届かない猫のために、プレイヤーが「いい高さの台」を作ってやるのがこの遊びの肝。
   */
  paw: number;
  coat: { body: string; patch: string };
}

export type CatMode = 'idle' | 'walk' | 'jump' | 'fall';
/** なぜその行き先を選んだか。デバッグで読むため（§6） */
export type GoalReason = 'food' | 'water' | 'bed' | 'play' | 'sunny' | 'perch' | 'wander' | 'none';
/**
 * 今その猫が何をしているか。
 * 猫は動いている時間より、じっとしている時間のほうがずっと長い生き物なので、
 * 「移動していない時に何をしているか」を厚くしてある。
 */
export type BusyKind =
  | 'eat'
  | 'drink'
  | 'sleep'
  /** 香箱を組んでじっとしている */
  | 'loaf'
  /** 毛づくろい */
  | 'groom'
  /** 伸びをする */
  | 'stretch'
  /** 外や下を眺めている */
  | 'watch'
  /** 置き去りの物にじゃれる */
  | 'play'
  /** 廃墟の飾りに前足を伸ばして叩き落とそうとしている */
  | 'swat'
  /** 猫同士でじゃれ合う */
  | 'tussle'
  /** 猫の集会。何匹かが同じ場所に集まって、ただ座っている */
  | 'meet'
  | null;

export interface CatState {
  id: number;
  species: string;
  /** 今いる（＝向かっている先の）ノード */
  node: number;
  /** 論理px。y は足元 */
  x: number;
  y: number;
  face: 1 | -1;
  mode: CatMode;
  /** 保持している経路。地形が変わったら必ず捨てる（§5.1） */
  path: number[] | null;
  step: number;
  from: number | null;
  to: number | null;
  t: number;
  dur: number;
  wait: number;
  goal: number | null;
  goalReason: GoalReason;
  /** 足場が変わって面食らっている残り秒。演出用（§5.1） */
  startled: number;
  /** じっとしている時間。描画のゆらぎに使う */
  sit: number;

  // ---- 帰巣（§2.7）。急かす要素ではない：間に合わなくても猫は失われない（§10.4）
  /** 最後に食べてからの秒数 */
  hunger: number;
  /** 最後に飲んでからの秒数 */
  thirst: number;
  /** 用事があるのに餌場・水場へ届かない状態か */
  stuck: boolean;
  /** 届かないまま過ごしている秒数。耳を伏せる演出に使う（§2.1） */
  blocked: number;
  /** 前の猫がどくのを待っている秒数。詰まりの表現に使う */
  waiting: number;
  /** 食べ／飲み／寝ている残り秒 */
  busy: number;
  busyKind: BusyKind;
  /** 次に痕跡（爪とぎ跡・抜け毛）を残すまでの秒数（§3.4 / §10.8） */
  nextTrace: number;
  /** 外から入ってきた直後の演出用（§3.6）。残り秒 */
  arriving: number;
  /** じゃれている相手（置き去りの物のマス）。無ければ null */
  toy: number | null;
  /** 叩こうとしている廃墟の飾りの id。無ければ null */
  swat: number | null;
  /** さっきまで寝ていた（起きたら伸びをする） */
  slept: boolean;
  /** 名前。プレイヤーが付けるのではなく、勝手に付いている */
  name: string;
  /**
   * 来歴の一行（§1 裏テーマ）。説明はしない。ここに書かれていること以上を語らない。
   * 最初から居る猫には無い（気づいたら居た、という体）。
   */
  history: string;
  /** 来歴に対応した見た目のしるし。無い場合は 'none' */
  mark: CatMark;
}

/** 見た目のしるし。台詞で説明せず、絵だけで示す（§1） */
export type CatMark = 'none' | 'collar' | 'earTip' | 'tailTip' | 'limp';

export interface HistoryEntry {
  line: string;
  mark: CatMark;
}

export const HISTORIES: readonly HistoryEntry[] = historyData.entries as HistoryEntry[];

/** 猫の名前。id から決まるので、同じ猫はいつでも同じ名前 */
export const CAT_NAMES: readonly string[] = nameData.names;

/** 置いてある複数マスの構造物 */
export interface BuildInstance {
  id: number;
  item: string;
  c: number;
  r: number;
  /** 左右反転して置いたか */
  flip: boolean;
}

/**
 * 思い出（§1 裏テーマ）。
 * 猫たちは、この建物が廃墟になる前からここに住んでいた。
 * 猫が長く過ごした場所に、その頃の物が見つかる。
 *
 * 出すのは**物の描写だけ**。誰が何をしたかは説明しない。
 * 台詞にした瞬間、猫がプレイヤーに語りかける存在になる（§1 / §2.3）。
 */
export interface MemoryInstance {
  id: string;
  /** sign = その場に残るだけ / readable = 読み返せる紙 / keepsake = 遺された物 */
  form: 'sign' | 'readable' | 'keepsake';
  /** 絵の種類 */
  kind: string;
  /** 何がきっかけで出るか。詳しくは discovery.ts */
  trigger: { on: 'dwell' | 'break' | 'swat' | 'floor'; salvage?: string; fixture?: string; floor?: string };
  /** 読み物にだけ付く見出し */
  title?: string;
  line: string;
  /** 出てくる場所。break / swat は壊れた場所に出るので、見つかるまで -1 */
  c: number;
  r: number;
  found: boolean;
  /** 猫がこの場所で過ごした累計秒。溜まると見つかる（dwell のみ） */
  dwell: number;
}

/**
 * 廃墟に残っている飾り（§3.4）。
 * 足場ではないので通行には関係しない。猫が前足で叩き落とすと資材になる。
 * プレイヤーは壊せない（§2.3）。落とす物を選べないのもわざと。
 */
export interface FixtureInstance {
  id: number;
  c: number;
  r: number;
  kind: string;
  /** 猫に叩かれた累計。kind の toughness を超えると落ちる */
  wear: number;
}

/**
 * 床に落ちている資材（§10.8）。
 * **猫は拾わない。** プレイヤーがタップで拾う。
 * 放っておいても消えないし、腐りもしない。急かす要素にはしない（§2.4）。
 */
export interface DropPile {
  c: number;
  r: number;
  mats: MaterialBundle;
}

export interface GameState {
  version: number;
  seed: number;
  rng: RngState;
  /** シミュレーション内の経過秒 */
  time: number;
  grid: Grid;
  /** 拾って手元にある資材（§10.8） */
  materials: Materials;
  /** クラフト済みで、まだ置いていない物。作るのと置くのを分けてある */
  inventory: Partial<Record<ItemId, number>>;
  /** 廃墟に残っている飾り */
  fixtures: FixtureInstance[];
  nextFixtureId: number;
  /** 床に落ちている資材。プレイヤーが拾うまでそこにある */
  drops: DropPile[];
  cats: CatState[];
  nextCatId: number;
  /** 餌場・水場。どちらも下層にしか無い（§2.7） */
  bowl: { c: number; r: number };
  water: { c: number; r: number };
  /** 日向の帯 */
  sun: { row: number; c0: number; c1: number };
  /** 外から猫が入ってくる場所（割れた窓）。§3.6 */
  entrance: { c: number; r: number };
  /**
   * マスごとの荒れ具合（爪とぎ跡・抜け毛）。長さ cols*rows。
   * 猫が居着いた場所が濃くなる。統計画面の代わりに読むもの（§3.5）
   */
  traces: number[];
  /** 快適な状態が続いている秒数。これが溜まると外から猫が来る（§3.6） */
  settle: number;
  /**
   * 次に来たがっている猫の種類（§3.3）。
   * 快適条件はこの猫の目で判定する。だから「この子には登れない」が
   * そのまま次の猫が来ない理由になる＝バリアフリー問題が成長の関門になる。
   * 窓の外に座って待っているのが見えるので、UIで説明する必要がない（§2.1）。
   */
  nextArrival: string;
  /** 次に快適条件を確かめるシム内時刻。毎tickやらない（§5.2） */
  nextComfortCheck: number;
  /**
   * 開通したフロアの id（§3.7）。
   * 上に行く理由を数値ではなく具体物にするため、初めて開通した時に
   * その階の元用途に応じた拾い物が現れる。一度きり。
   */
  openedFloors: string[];
  /** 使えるようになった設置物 */
  unlockedItems: string[];
  /** 置いてある複数マスの構造物（キャットタワーなど） */
  builds: BuildInstance[];
  nextBuildId: number;
  /** 見つかった思い出（§1 裏テーマ）。猫が過ごした場所に現れる */
  memories: MemoryInstance[];
}

export const SPECIES: readonly Species[] = catsData.species as Species[];

/** 置き去りにされている物の種類（§3.4） */
export interface SalvageKind {
  id: string;
  name: string;
  /** 壊すと出てくる資材。種類ごとに採れる物が違う */
  yield: MaterialBundle;
  /** 壊れるまでに必要な、猫がじゃれた量 */
  toughness: number;
}

/** 廃墟の飾りの種類 */
export interface FixtureKind {
  id: string;
  name: string;
  /** wall = 奥の壁に掛かっている / ceiling = 天井から下がっている */
  mount: 'wall' | 'ceiling';
  toughness: number;
  yield: MaterialBundle;
}
export const FIXTURE_KINDS: readonly FixtureKind[] = fixtureData.kinds as FixtureKind[];

export function fixtureKindOf(id: string): FixtureKind | null {
  return FIXTURE_KINDS.find((k) => k.id === id) ?? null;
}
export const SALVAGE_KINDS: readonly SalvageKind[] = salvageData.kinds as SalvageKind[];

export function salvageKindOf(index: number): SalvageKind | null {
  return index > 0 ? (SALVAGE_KINDS[index - 1] ?? null) : null;
}

export function speciesById(id: string): Species {
  const s = SPECIES.find((x) => x.id === id);
  if (!s) throw new Error(`unknown species: ${id}`);
  return s;
}

/** 地形は map（文字絵）が唯一の出どころ。# = 固い / . = 空き */
export const MAP_ROWS: readonly string[] = building.map;
export const MAP_COLS = MAP_ROWS[0].length;
export const MAP_HEIGHT = MAP_ROWS.length;

export function mapSolidAt(c: number, r: number): boolean {
  if (r < 0 || r >= MAP_HEIGHT || c < 0 || c >= MAP_COLS) return true;
  return MAP_ROWS[r][c] === '#';
}

/**
 * その行のスラブに開いている穴の列。
 * 座標を別に持たず、map から読む。描き変えた時にズレないようにするため。
 */
export function slabGapsAt(row: number): number[] {
  if (row < 0 || row >= MAP_HEIGHT) return [];
  const out: number[] = [];
  for (let c = 1; c < MAP_COLS - 1; c++) if (MAP_ROWS[row][c] === '.') out.push(c);
  return out;
}

/** 廃ビルを組み立てる。建物は最初から全フロア存在する（§3.1） */
export function createBuilding(): Grid {
  const cols = MAP_COLS;
  const rows = MAP_HEIGHT;
  const grid: Grid = {
    cols,
    rows,
    tiles: new Array(cols * rows).fill(EMPTY),
    furniture: new Array(cols * rows).fill(0),
    salvage: new Array(cols * rows).fill(0),
    wear: new Array(cols * rows).fill(0),
    build: new Array(cols * rows).fill(0),
    decor: new Array(cols * rows).fill(0),
  };

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      setTile(grid, c, r, MAP_ROWS[r][c] === '#' ? STRUCTURE : EMPTY);
    }
  }

  // 置き去りにされている物。固いので足場になる（§3.4）
  for (const sv of building.salvage) {
    const kind = SALVAGE_KINDS.findIndex((k) => k.id === sv.kind);
    if (kind < 0) continue;
    setTile(grid, sv.c, sv.r, SALVAGE);
    grid.salvage[keyOf(grid, sv.c, sv.r)] = kind + 1;
  }

  return grid;
}

export function makeCat(grid: Grid, id: number, species: string, c: number, r: number): CatState {
  return {
    id,
    species,
    node: keyOf(grid, c, r),
    x: c * TILE + TILE / 2,
    y: (r + 1) * TILE,
    face: 1,
    mode: 'idle',
    path: null,
    step: 0,
    from: null,
    to: null,
    t: 0,
    dur: 0,
    wait: 0.4 + (id % 5) * 0.3,
    goal: null,
    goalReason: 'none',
    startled: 0,
    sit: 0,
    hunger: (id % 7) * 6,
    thirst: (id % 5) * 5,
    stuck: false,
    blocked: 0,
    waiting: 0,
    busy: 0,
    busyKind: null,
    nextTrace: 20 + (id % 9) * 5,
    arriving: 0,
    toy: null,
    swat: null,
    slept: false,
    name: CAT_NAMES[(id * 7 + 3) % CAT_NAMES.length],
    history: '',
    mark: 'none',
  };
}

export function createInitialState(seed = 20260817): GameState {
  const grid = createBuilding();
  const cats: CatState[] = building.startCats.map((c, i) => makeCat(grid, i + 1, c.species, c.c, c.r));
  return {
    version: STATE_VERSION,
    seed,
    rng: createRng(seed),
    time: 0,
    grid,
    materials: { ...emptyMaterials(), ...building.startMaterials },
    inventory: {},
    fixtures: building.fixtures.map((f, i) => ({ id: i + 1, c: f.c, r: f.r, kind: f.kind, wear: 0 })),
    nextFixtureId: building.fixtures.length + 1,
    drops: [],
    cats,
    nextCatId: cats.length + 1,
    bowl: { ...building.bowl },
    water: { ...building.water },
    sun: { ...building.sun },
    entrance: { ...building.entrance },
    traces: new Array(grid.cols * grid.rows).fill(0),
    settle: 0,
    nextArrival: 'stray',
    nextComfortCheck: 0,
    openedFloors: ['1f'],
    unlockedItems: ['box', 'bed', 'desk', 'bookshelf', 'frame', 'pennant', 'tower'],
    builds: [],
    nextBuildId: 1,
    memories: (memoryData.spots as unknown as MemoryInstance[]).map((m) => ({
      ...m,
      c: m.c ?? -1,
      r: m.r ?? -1,
      found: false,
      dwell: 0,
    })),
  };
}

/** セーブ用。構造をまるごとコピーする（§4.4） */
export function serialize(state: GameState): string {
  return JSON.stringify(state);
}

export function deserialize(json: string): GameState | null {
  try {
    const s = JSON.parse(json) as GameState;
    if (!s || s.version !== STATE_VERSION) return null;
    if (!s.grid || !Array.isArray(s.grid.tiles) || !Array.isArray(s.cats)) return null;
    if (!Array.isArray(s.grid.furniture) || !Array.isArray(s.traces)) return null;
    if (!Array.isArray(s.grid.wear) || !Array.isArray(s.grid.salvage)) return null;
    if (!Array.isArray(s.grid.build) || !Array.isArray(s.builds)) return null;
    if (!Array.isArray(s.grid.decor) || !Array.isArray(s.memories)) return null;
    if (!Array.isArray(s.fixtures) || !Array.isArray(s.drops)) return null;
    if (!s.materials || !s.inventory) return null;
    return s;
  } catch {
    return null;
  }
}

export function cloneState(state: GameState): GameState {
  return JSON.parse(JSON.stringify(state)) as GameState;
}

/** floors.json をそのまま参照したい箇所（描画のフロア表示など）向け */
export const BUILDING = building;
